import { A } from "@solidjs/router";
import { For, Show, type VoidComponent } from "solid-js";
import { isEmpty } from "lodash-es";
import { useTranslation } from "../../../../state/i18n";
import { useCustomerState } from "../../../../state/customer";
import Text from "../../../../components/text/Text";
import Pill from "../../../../components/pill/Pill";
import Popover from "../../../../components/popover/Popover";
import { InformationButton } from "../../../../components/button/InformationButton";
import type { Group } from "../../../../state/group";
import { GroupAvatar } from "../../../../components/avatar/Avatar.tsx";

type GroupSection = { label?: string; groups: Group[] };

const GroupLink: VoidComponent<{ group: Group }> = (props) => {
  const customer = useCustomerState();

  return (
    <A
      href={`/customers/${customer.customerId}/groups/${props.group.id}`}
      class="flex w-full items-center gap-2 rounded-sm p-2 text-small-medium text-primary hover:bg-surface-hover focus-visible:outline-default"
    >
      <GroupAvatar group={props.group} size="xs" />
      <span class="min-w-0 truncate">{props.group.name}</span>
    </A>
  );
};

export const GroupsPopover: VoidComponent<{ sections: GroupSection[]; title?: string; popoverTitle?: string }> = (
  props,
) => {
  const { t } = useTranslation();

  const title = () => props.title ?? t(($) => $.USER_PAGE_INHERITED_VIA_GROUPS);

  return (
    <Show when={!isEmpty(props.sections.flatMap((section) => section.groups))}>
      <Popover
        variant="md"
        positioning={{ placement: "bottom-end" }}
        aria-label={title()}
        Title={() => (
          <Text
            as="h3"
            truncate
            text={props.popoverTitle ?? t(($) => $.USER_PAGE_INHERITED_VIA_GROUPS)}
            class="text-header-md text-primary"
          />
        )}
        Trigger={(triggerProps) => <InformationButton text={title()} {...triggerProps} />}
      >
        <div class="-mx-2 mt-1 flex flex-col">
          <For each={props.sections}>
            {(section, index) => (
              <Show when={!isEmpty(section.groups)}>
                <Show when={section.label}>
                  {(label) => (
                    <div
                      class="mb-2 flex min-w-0 items-center gap-1 px-2"
                      classList={{ "mt-1": index() === 0, "mt-4": index() !== 0 }}
                    >
                      <Text text={t(($) => $.USER_PAGE_INHERITED)} class="text-base-regular text-secondary" />
                      <Pill label={label()} variant="neutral" />
                      <Text text={t(($) => $.USER_PAGE_VIA)} class="text-base-regular text-secondary" />
                    </div>
                  )}
                </Show>
                <For each={section.groups}>{(group) => <GroupLink group={group} />}</For>
              </Show>
            )}
          </For>
        </div>
      </Popover>
    </Show>
  );
};
